import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Fade,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import { Business } from '../../../data/types/domain';
import { createOrUpdateBusinessListing } from '../../../domain/usecases/usecases';
import { BusinessDetailsStep, BusinessDetailsStepRef } from './BusinessDetailsStep';
import { ServiceLocationStep, ServiceLocationStepRef } from './ServiceLocationStep';
import { ListingPolicyStep, ListingPolicyStepRef } from './ListingPolicyStep';
import { ShippingPolicyStep, ShippingPolicyStepRef } from './ShippingPolicyStep';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { BusinessListingForm, BusinessListingResult } from '../../../data/types/application';
import { useNotification, AppNotification } from '../../../components/Notification';
import { FormProvider, useFormContext } from '../context/FormContext';
import { PageGradient } from '../../../components/PageGradient';
import { alpha } from '@mui/material/styles';
import { extractErrorMessage } from '../../../utils/common';

interface BusinessFormProps {
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
  initialData?: BusinessListingForm | BusinessListingResult;
  title?: string;
  isEditMode?: boolean;
}

const steps = ['Business Details', 'Service Location', 'Listing Policy', 'Shipping Policy'];

const BusinessFormContent: React.FC<BusinessFormProps> = ({
  open,
  onClose,
  initialData,
  title,
  isEditMode,
  onUpdate,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { notification, showNotification, hideNotification } = useNotification();
  const { formData, updateFormData, resetFormData } = useFormContext();

  // Reset state when dialog closes
  const handleClose = () => {
    setActiveStep(0);
    resetFormData(); // Reset form data when closing
    onClose();
  };

  // Add useEffect to reset form data when dialog opens or initialData changes
  useEffect(() => {
    if (open) {
      resetFormData(); // Reset previous data when dialog opens
      setActiveStep(0);
      
      // If we have initialData (edit mode), set it in the form data
      if (initialData) {
        if (initialData.business) {
          updateFormData(0, initialData.business);
        }
        if (initialData.serviceLocations) {
          updateFormData(1, initialData.serviceLocations);
        }
        if (initialData.listingPolicy) {
          updateFormData(2, initialData.listingPolicy);
        }
        if (initialData.shippingPolicies) {
          updateFormData(3, initialData.shippingPolicies);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialData]); // Intentionally omitting resetFormData and updateFormData to prevent resetting when adding countries/cities

  // Step refs
  const businessDetailsRef = useRef<BusinessDetailsStepRef>(null);
  const serviceLocationRef = useRef<ServiceLocationStepRef>(null);
  const listingPolicyRef = useRef<ListingPolicyStepRef>(null);
  const shippingPolicyRef = useRef<ShippingPolicyStepRef>(null);

  const getCurrentStepRef = () => {
    switch (activeStep) {
      case 0:
        return businessDetailsRef;
      case 1:
        return serviceLocationRef;
      case 2:
        return listingPolicyRef;
      case 3:
        return shippingPolicyRef;
      default:
        return null;
    }
  };

  const handleNext = async () => {
    const currentRef = getCurrentStepRef();
    if (!currentRef?.current) {
      setActiveStep((prevStep) => prevStep + 1);
      return;
    }

    const isValid = await currentRef.current.isValid();
    if (isValid) {
      const data = await currentRef.current.getData();
      updateFormData(activeStep, data);
      setActiveStep((prevStep) => prevStep + 1);
    } else {
      const errors = await currentRef.current.getErrors();
      showNotification(errors.join(', '), 'error');
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };


  /**
   * Handle the submit button click
   * 
   * If the business is in edit mode, we need to update the business
   * otherwise, we need to create a new business.
   */
  const handleSubmit = async () => {
    const currentRef = getCurrentStepRef();
    if (!currentRef?.current) return;

    const isValid = await currentRef.current.isValid();
    if (!isValid) {
      const errors = await currentRef.current.getErrors();
      showNotification(errors.join(', '), 'error');
      return;
    }

    // Get the final step data
    const data = await currentRef.current.getData();
    updateFormData(activeStep, data);

    setError(null);
    setLoading(true);

    try {
      await createOrUpdateBusinessListing({
        businessListing: {
          business: formData.current.business!,
          listingPolicy: formData.current.listingPolicy!,
          shippingPolicies: formData.current.shippingPolicy!,
          serviceLocations: formData.current.serviceLocation!,
        },
        isEditMode: isEditMode,
        business_id: isEditMode ? (initialData as BusinessListingResult).business.id : undefined,
      });

      showNotification(isEditMode ? 'Business updated successfully' : 'Business created successfully', 'success');
      resetFormData();
      setActiveStep(0);
      onClose();
      onUpdate();
    } catch (err) {
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step: number) => {
    // Generate a consistent key that only changes when the dialog opens/closes
    // Not when other state (like formData) changes
    const componentKey = `form-${open ? 'open' : 'closed'}-${isEditMode ? 'edit' : 'add'}`;
    
    switch (step) {
      case 0:
        return (
          <BusinessDetailsStep
            ref={businessDetailsRef}
            initialData={formData.current.business || (initialData?.business && isEditMode ? initialData.business : undefined)}
            key={`business-details-${componentKey}`}
          />
        );
      case 1:
        return (
          <ServiceLocationStep
            ref={serviceLocationRef}
            initialData={formData.current.serviceLocation || (initialData?.serviceLocations && isEditMode ? initialData.serviceLocations : undefined)}
            key={`service-location-${componentKey}`}
          />
        );
      case 2:
        return (
          <ListingPolicyStep
            ref={listingPolicyRef}
            initialData={formData.current.listingPolicy || (initialData?.listingPolicy && isEditMode ? initialData.listingPolicy : undefined)}
            key={`listing-policy-${componentKey}`}
          />
        );
      case 3:
        return (
          <ShippingPolicyStep
            ref={shippingPolicyRef}
            initialData={formData.current.shippingPolicy || (initialData?.shippingPolicies && isEditMode ? initialData.shippingPolicies : undefined)}
            key={`shipping-policy-${componentKey}`}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        fullScreen
        PaperProps={{
          sx: {
            borderRadius: 0,
            minHeight: '100vh',
            position: 'relative',
            overflow: 'hidden',
          }
        }}
      >
        <PageGradient />
        
        <DialogTitle sx={{ 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          position: 'relative',
          zIndex: 1,
        }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
            {title}
          </Typography>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={handleClose} 
            aria-label="close"
            sx={{ ml: 2 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ 
          p: 0, 
          position: 'relative', 
          display: 'flex', 
          flexDirection: 'column', 
          height: 'calc(100vh - 120px)',
          overflow: 'hidden',
          zIndex: 1,
        }}>
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto',
            p: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            position: 'relative',
          }}>
            <Fade in={true} timeout={500}>
              <Box sx={{ 
                width: '100%', 
                maxWidth: 800,
                backdropFilter: 'blur(2px)',
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.3),
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
              }}>
                {/* Desktop Stepper */}
                <Stepper 
                  activeStep={activeStep} 
                  alternativeLabel 
                  sx={{ 
                    mb: 4,
                    display: { xs: 'none', sm: 'flex' },
                    '& .MuiStepLabel-label': { 
                      fontSize: '0.75rem',
                      '&.Mui-active': {
                        fontWeight: 'bold',
                        color: 'info.dark'
                      },
                      '&.Mui-completed': {
                        color: 'success.main',
                        fontWeight: 600
                      }
                    },
                    '& .MuiStepIcon-root': { 
                      color: (theme) => alpha(theme.palette.grey[400], 0.7),
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
                      '&.Mui-active': {
                        color: (theme) => theme.palette.info.dark,
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                        transform: 'scale(1.2)'
                      },
                      '&.Mui-completed': {
                        color: (theme) => theme.palette.success.main
                      }
                    },
                    '& .MuiStepIcon-text': { 
                      fill: (theme) => theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette.common.white,
                      fontWeight: 'bold'
                    },
                    '& .MuiStepConnector-line': {
                      borderColor: (theme) => alpha(theme.palette.divider, 0.6)
                    },
                    '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                      borderColor: 'primary.main'
                    },
                    '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                      borderColor: 'success.main'
                    }
                  }}
                >
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
                
                {/* Mobile Stepper */}
                <Box sx={{ 
                  display: { xs: 'flex', sm: 'none' }, 
                  mb: 3,
                  flexDirection: 'column',
                  width: '100%'
                }}>
                  {/* Step indicator */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: (theme) => alpha(theme.palette.background.paper, 0.7),
                    p: 1.5,
                    borderRadius: 1,
                    backdropFilter: 'blur(5px)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    mb: 2
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      Step {activeStep + 1} of {steps.length}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                      {steps[activeStep]}
                    </Typography>
                  </Box>
                  
                  {/* Visual progress indicator */}
                  <Box sx={{
                    display: 'flex',
                    width: '100%',
                    height: 6,
                    borderRadius: 3,
                    bgcolor: (theme) => alpha(theme.palette.grey[300], 0.5),
                    overflow: 'hidden',
                  }}>
                    {steps.map((_, index) => (
                      <Box 
                        key={index}
                        sx={{
                          flex: 1,
                          height: '100%',
                          bgcolor: index < activeStep 
                            ? 'success.main' 
                            : index === activeStep 
                              ? 'primary.main' 
                              : 'transparent',
                          ml: index > 0 ? 0.5 : 0,
                          transition: 'background-color 0.3s ease',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    mb: 4, 
                    textAlign: 'center',
                    fontWeight: 'bold',
                    display: { xs: 'none', sm: 'block' },
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -8,
                      left: '50%',
                      width: '60px',
                      height: '3px',
                      backgroundColor: 'primary.main',
                      transform: 'translateX(-50%)',
                      borderRadius: '2px',
                      boxShadow: (theme) => `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}`,
                    },
                    color: 'primary.main',
                    animation: 'fadeIn 0.5s ease-in-out',
                    '@keyframes fadeIn': {
                      '0%': {
                        opacity: 0,
                        transform: 'translateY(-10px)'
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'translateY(0)'
                      }
                    }
                  }}
                >
                  {steps[activeStep]}
                </Typography>
                
                <Box sx={{ 
                  width: '100%',
                  bgcolor: 'background.paper',
                  borderRadius: { xs: 0, sm: 2 },
                  boxShadow: { xs: 'none', sm: '0 4px 20px rgba(0,0,0,0.08)' },
                  p: { xs: 0, sm: 3 },
                  position: 'relative',
                  zIndex: 1,
                  backdropFilter: 'blur(5px)',
                  border: (theme) => `1px solid ${theme.palette.divider}`
                }}>
                  {getStepContent(activeStep)}
                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {error}
                    </Alert>
                  )}
                </Box>
              </Box>
            </Fade>
          </Box>
          
          <DialogActions sx={{ 
            p: { xs: 1, sm: 2 }, 
            borderTop: '1px solid', 
            borderColor: 'divider',
            justifyContent: 'space-between',
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            zIndex: 2,
            boxShadow: '0 -4px 6px rgba(0,0,0,0.05)',
          }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {activeStep > 0 && (
                <Button
                  onClick={handleBack}
                  disabled={loading}
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  size={isMobile ? "small" : "medium"}
                >
                  Back
                </Button>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              {activeStep === steps.length - 1 ? (
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  endIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
                  size={isMobile ? "small" : "medium"}
                >
                  Submit
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  variant="contained"
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                  size={isMobile ? "small" : "medium"}
                >
                  Next
                </Button>
              )}
            </Box>
          </DialogActions>
        </DialogContent>
      </Dialog>
      
      <AppNotification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </>
  );
};

export const BusinessForm: React.FC<BusinessFormProps> = (props) => {
  return (
    <FormProvider>
      <BusinessFormContent {...props} />
    </FormProvider>
  );
}; 